import { Component, ChangeDetectionStrategy, input, output, signal, computed } from '@angular/core';
import { CommonModule } from '@angular/common';

import { MetricCardComponent } from 'src/app/core/components/metric-card/metric-card.component';
import {
  PolicyDocument,
  AggregateReport,
  ALIGNMENT_LABEL_CONFIG,
  DOMAIN_DISPLAY_NAMES,
  ControlDomain,
} from '../../services/policy-gap-analysis.service';
import { FindingStatusBadgeComponent } from '../finding-status-badge/finding-status-badge.component';
import { DocumentUploadDialogComponent } from '../document-upload-dialog/document-upload-dialog.component';

@Component({
  selector: 'app-repository-hub',
  standalone: true,
  imports: [CommonModule, MetricCardComponent, FindingStatusBadgeComponent, DocumentUploadDialogComponent],
  templateUrl: './repository-hub.component.html',
  styleUrl: './repository-hub.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class RepositoryHubComponent {
  documents = input.required<PolicyDocument[]>();
  aggregateReport = input<AggregateReport | null>(null);
  isLoading = input<boolean>(true);

  documentSelected = output<PolicyDocument>();
  viewAggregate = output<void>();
  documentUploaded = output<PolicyDocument>();

  public showUploadDialog = signal<boolean>(false);

  public analyzedDocCount = computed(() => this.documents().filter((d) => d.status === 'analyzed').length);

  public totalValidated = computed(() => this.aggregateReport()?.report_card.validated_count ?? 0);
  public totalMismatches = computed(() => this.aggregateReport()?.report_card.documented_not_validated_count ?? 0);
  public totalRecommended = computed(() => this.aggregateReport()?.report_card.recommended_not_documented_count ?? 0);
  public totalUnverifiable = computed(() => {
    const rc = this.aggregateReport()?.report_card;
    return (rc?.not_verifiable_count ?? 0) + (rc?.potentially_verifiable_count ?? 0);
  });

  public getAlignmentConfig(label: string) {
    return ALIGNMENT_LABEL_CONFIG[label];
  }

  public getDomainDisplayName(domain: ControlDomain): string {
    return DOMAIN_DISPLAY_NAMES[domain];
  }

  public getStatusLabel(status: string): string {
    const map: Record<string, string> = {
      uploaded: 'Uploaded',
      processing: 'Processing...',
      analyzed: 'Analyzed',
      error: 'Error',
    };
    return map[status] || status;
  }

  public getStatusClass(status: string): string {
    const map: Record<string, string> = {
      uploaded: 'bg-secondary',
      processing: 'bg-info',
      analyzed: 'bg-success',
      error: 'bg-danger',
    };
    return map[status] || 'bg-secondary';
  }

  public onDocumentClick(doc: PolicyDocument): void {
    if (doc.status === 'analyzed') {
      this.documentSelected.emit(doc);
    }
  }

  public onUploadComplete(doc: PolicyDocument): void {
    this.showUploadDialog.set(false);
    this.documentUploaded.emit(doc);
  }

  public formatDate(dateStr: string): string {
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  }
}
